import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { DueDateBaseDate } from '@main-application/inspections/models/rest-inspections-model.interface';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { isExist } from '@shared/utils/exist.util';
import { InputModule } from '@ui-components/controls/input/input.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';

import { CustomNotificationFieldType } from '../custom-notifications-dialog/custom-notifications-dialog.component';

export type CustomRemindDialogFieldType = CustomNotificationFieldType | 'dueDate';

@Component({
  standalone: true,
  selector: 'app-custom-remind-dialog',
  templateUrl: './custom-remind-dialog.component.html',
  styleUrls: [
    '../custom-notifications-dialog/custom-notifications-dialog.component.scss',
    './custom-remind-dialog.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, InputModule, MatDialogModule, RadioButtonDropdownModule, MatButtonModule],
})
export class CustomRemindDialogComponent implements OnInit {
  private dialogRef: MatDialogRef<CustomRemindDialogComponent> = inject(MatDialogRef);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  title = 'Custom Remind on';
  public data: {
    fieldType: CustomRemindDialogFieldType;
    existingBeforeAfter?: string;
    existingDays?: number;
    stepName?: string;
    existingDueDateBaseDate?: DueDateBaseDate;
  } = inject(MAT_DIALOG_DATA);

  beforeAfterItems: IRadioButtonOption<number>[] = [
    {
      value: -1,
      label: `Before the due date`,
    },
    {
      value: 1,
      label: `After the due date`,
    },
  ];

  beforeAfterControl = new UntypedFormControl(-1);
  daysControl = new UntypedFormControl(null, [
    Validators.required,
    Validators.max(1000),
    Validators.min(1),
    Validators.pattern(/^\d+$/),
  ]);
  formSubmitted = false;
  DueDateBaseDate = DueDateBaseDate;
  dividerPosition: number | null = null;

  ngOnInit(): void {
    this.title = this.setTitle(this.data.fieldType);
    if (this.data.fieldType === 'dueDate') {
      if (this.data.stepName) {
        this.beforeAfterItems = [
          {
            value: -1,
            label: `Before ${this.data.stepName}`,
          },
          {
            value: 1,
            label: `After ${this.data.stepName}`,
          },
          {
            value: -4,
            label: `After Inspection Creation Date`,
          },
        ];

        this.dividerPosition = 1;
      }

      if (this.data.existingDueDateBaseDate === DueDateBaseDate.CreationDate) {
        if (this.data.existingBeforeAfter === 'after') {
          this.beforeAfterControl.setValue(-4);
        }
      } else {
        if (this.data.existingBeforeAfter) {
          this.beforeAfterControl.setValue(this.data.existingBeforeAfter === 'before' ? -1 : 1);
        }
      }

      if (isExist(this.data.existingDays)) {
        this.daysControl.setValue(this.data.existingDays);
      }
    } else {
      if (this.data.stepName && (this.data.fieldType === 'notifyOn' || this.data.fieldType === 'remindOn')) {
        this.beforeAfterItems = [
          {
            value: -1,
            label: `Before ${this.data.stepName}`,
          },
          {
            value: 1,
            label: `After ${this.data.stepName}`,
          },
        ];
      }

      if (this.data.fieldType === 'notifyOn' || this.data.fieldType === 'remindOn') {
        if (this.data.existingBeforeAfter) {
          this.beforeAfterControl.setValue(this.data.existingBeforeAfter === 'before' ? -1 : 1);
        } else {
          this.beforeAfterControl.setValue(-1);
        }

        if (isExist(this.data.existingDays)) {
          this.daysControl.setValue(this.data.existingDays);
        } else {
          this.daysControl.setValue(14);
        }
      } else if (this.data.fieldType === 'thenRemindOn' || this.data.fieldType === 'until') {
        if (isExist(this.data.existingDays)) {
          this.daysControl.setValue(this.data.existingDays);
        }
      }
    }
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    this.formSubmitted = true;

    if (this.data.fieldType === 'dueDate') {
      const selectedValue = this.beforeAfterControl.value;

      if (selectedValue === null || selectedValue === undefined) {
        return;
      }

      if (this.daysControl.valid) {
        const isCreationDateOption = selectedValue === -4;
        const dueDateBaseDate = isCreationDateOption
          ? DueDateBaseDate.CreationDate
          : DueDateBaseDate.DynamicDateOrCreationDateIfDateInThePast;

        let beforeAfter: string;
        if (selectedValue === -4) {
          beforeAfter = 'after';
        } else {
          beforeAfter = selectedValue === -1 ? 'before' : 'after';
        }

        this.dialogRef.close({
          beforeAfter,
          days: +this.daysControl.value,
          dueDateBaseDate,
        });
      }
    } else {
      if (this.daysControl.valid) {
        const result =
          this.data.fieldType === 'thenRemindOn' || this.data.fieldType === 'until'
            ? +this.daysControl.value
            : +this.daysControl.value * this.beforeAfterControl.value;
        this.dialogRef.close({
          days: result,
        });
      }
    }
  }

  private setTitle(fieldType: CustomRemindDialogFieldType): string {
    switch (fieldType) {
      case 'notifyOn':
        return 'Create on';
      case 'remindOn':
        return 'Remind on';
      case 'thenRemindOn':
        return 'Then Remind on';
      case 'until':
        return 'Until';
      case 'dueDate':
        return 'Due';
    }
    return 'Notify on';
  }
}
